import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Star, EyeOff, GripVertical } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const EMPTY = { author: "", rating: 5, text: "", date_label: "", source: "Google", visible: true };

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`star-${n}`}
          className="transition-transform hover:scale-110">
          <Star className={`w-6 h-6 ${n <= value ? "fill-amber-400 text-amber-400" : "text-[#E8E2D9]"}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => api.get("/admin/reviews").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ author: r.author, rating: r.rating || 5, text: r.text || "",
      date_label: r.date_label || "", source: r.source || "Google", visible: r.visible !== false });
    setOpen(true);
  };

  const save = async () => {
    if (!form.author.trim()) { toast.error("Naam is verplicht."); return; }
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (editing) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success(editing ? "Review bijgewerkt." : "Review toegevoegd.");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Review van "${r.author}" verwijderen?`)) return;
    await api.delete(`/admin/reviews/${r.id}`);
    toast.success("Review verwijderd."); load();
  };

  const persistOrder = async (ordered) => {
    setList(ordered);
    try { await api.put("/admin/reviews/reorder", { ids: ordered.map((r) => r.id) }); }
    catch { toast.error("Volgorde opslaan mislukt."); load(); }
  };

  const onDropRow = (target) => {
    if (!dragId || dragId === target.id) { setDragId(null); return; }
    const dragged = list.find((r) => r.id === dragId);
    const without = list.filter((r) => r.id !== dragId);
    const idx = without.findIndex((r) => r.id === target.id);
    without.splice(idx, 0, dragged);
    setDragId(null);
    persistOrder(without);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-3xl font-medium tracking-tight">Reviews</h1>
        <Button onClick={openNew} data-testid="add-review-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">
          <Plus className="w-4 h-4 mr-1" /> Nieuwe review
        </Button>
      </div>
      <p className="text-ink-muted mb-8">Voeg hier reviews toe die op de website worden getoond. Sleep om de volgorde te wijzigen.</p>

      {!list.length && <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 text-center text-ink-muted text-sm">Nog geen reviews. Voeg je eerste review toe.</div>}

      <div className="space-y-3">
        {list.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`}
            draggable onDragStart={() => setDragId(r.id)} onDragEnd={() => setDragId(null)}
            onDragOver={(e) => e.preventDefault()} onDrop={() => onDropRow(r)}
            className={`flex items-start gap-3 bg-white rounded-2xl border border-[#E8E2D9] p-4 shadow-[0_8px_30px_rgb(74,63,63,0.04)] ${dragId === r.id ? "opacity-50" : ""}`}>
            <span className="cursor-grab active:cursor-grabbing text-ink-muted hover:text-brand mt-1" data-testid={`drag-review-${r.id}`}><GripVertical className="w-4 h-4" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{r.author}</span>
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-[#E8E2D9]"}`} strokeWidth={1.5} />
                  ))}
                </span>
                {r.date_label && <span className="text-xs text-ink-muted">· {r.date_label}</span>}
                {r.visible === false && <span className="text-[10px] flex items-center gap-1 text-ink-muted bg-[#F2EBE1] rounded-full px-2 py-0.5"><EyeOff className="w-3 h-3" /> Verborgen</span>}
              </div>
              {r.text && <p className="text-sm text-ink-muted mt-1 leading-snug">{r.text}</p>}
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-review-${r.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={`delete-review-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Review bewerken" : "Nieuwe review"}</DialogTitle>
            <DialogDescription>Deze review wordt op de website getoond wanneer zichtbaar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Naam klant *</Label><Input data-testid="review-author" value={form.author} onChange={set("author")} placeholder="bijv. Sanne" className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm text-ink-muted mb-2 block">Beoordeling</Label><Stars value={Number(form.rating)} onChange={(n) => setForm({ ...form, rating: n })} /></div>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Review tekst</Label><Textarea data-testid="review-text" value={form.text} onChange={set("text")} rows={4} placeholder="Wat schreef de klant..." className="rounded-xl border-[#E8E2D9]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Datum (label)</Label><Input data-testid="review-date" value={form.date_label} onChange={set("date_label")} placeholder="bijv. 2 maanden geleden" className="rounded-xl border-[#E8E2D9]" /></div>
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Bron</Label><Input data-testid="review-source" value={form.source} onChange={set("source")} placeholder="Google" className="rounded-xl border-[#E8E2D9]" /></div>
            </div>
            <div className="rounded-xl border border-[#E8E2D9] flex items-center justify-between p-3">
              <div className="text-sm">Zichtbaar op website</div>
              <Switch data-testid="review-visible" checked={form.visible} onCheckedChange={(v) => setForm({ ...form, visible: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annuleren</Button>
            <Button onClick={save} data-testid="save-review-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
